/**
 * Horarios por momento (5 fijos) + Observaciones operativas del evento.
 * (2026-10) Fase 1 — ver /app/memory/PRD.md.
 * Independiente de horario_inicio/horario_fin (inicio/fin del baile).
 */
import React from 'react';
import { Clock, AlertTriangle, Info, Plus, Trash2, CalendarClock, CheckSquare, CalendarIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Calendar } from './ui/calendar';
import { getFaseIcon } from './EventSpaces';

export const FIXED_MOMENTS = ['Ceremonia', 'Cóctel', 'Banquete/Comida', 'DJ/Baile', 'Cierre'];

/** Compara horarios seleccionados contra los que ya tiene el evento; devuelve
 * una lista de strings legibles para avisar antes de sobrescribir. */
export const buildOverwriteWarning = (selectedHorariosList = [], currentHorarios = []) => {
  return selectedHorariosList
    .map((h) => {
      const prev = (currentHorarios || []).find(
        (e) => e.momento === h.momento && !!e.es_extra === !!h.es_extra
      );
      const prevFilled = prev && (prev.hora_inicio || prev.hora_fin);
      const changed = prevFilled && (prev.hora_inicio !== h.hora_inicio || prev.hora_fin !== h.hora_fin);
      if (!changed) return null;
      const prevTxt = `${prev.hora_inicio || '–'}${prev.hora_fin ? `–${prev.hora_fin}` : ''}`;
      const nextTxt = `${h.hora_inicio || '–'}${h.hora_fin ? `–${h.hora_fin}` : ''}`;
      return `${h.momento}: ${prevTxt} → ${nextTxt}`;
    })
    .filter(Boolean);
};

/* ============== EDITOR manual (ficha del evento, sin documento de origen) ============== */
export const EventScheduleNotesEditor = ({
  horarios = [],
  onChangeHorarios,
  observaciones = [],
  onChangeObservaciones,
}) => {
  const getHorario = (momento) =>
    horarios.find((h) => h.momento === momento) || { momento, hora_inicio: '', hora_fin: '' };

  const updateHorario = (momento, field, value) => {
    const idx = horarios.findIndex((h) => h.momento === momento);
    const current = idx >= 0 ? { ...horarios[idx] } : { momento, hora_inicio: '', hora_fin: '' };
    current[field] = value;
    const next = idx >= 0 ? horarios.map((h, i) => (i === idx ? current : h)) : [...horarios, current];
    onChangeHorarios(next);
  };

  const updateObs = (idx, field, value) => {
    const next = observaciones.map((o, i) => (i === idx ? { ...o, [field]: value } : o));
    onChangeObservaciones(next);
  };
  const addObs = () => {
    onChangeObservaciones([...observaciones, { id: `manual-${Date.now()}`, texto: '', relevante: false, implicacion: '' }]);
  };
  const removeObs = (idx) => {
    onChangeObservaciones(observaciones.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs text-slate-400 mb-2">Hora de inicio y fin de cada momento fijo del evento</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          {FIXED_MOMENTS.map((momento) => {
            const h = getHorario(momento);
            const { Icon, color, bg } = getFaseIcon(momento);
            return (
              <div
                key={momento}
                className={`rounded-lg border border-white/10 p-2 space-y-1.5 ${bg}`}
                data-testid={`schedule-editor-momento-${momento}`}
              >
                <div className="flex items-center gap-1.5">
                  <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                  <span className={`text-xs font-semibold truncate ${color}`}>{momento}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    value={h.hora_inicio}
                    onChange={(e) => updateHorario(momento, 'hora_inicio', e.target.value)}
                    placeholder="HH:MM"
                    className="bg-slate-900 border-slate-700 text-white text-xs h-8"
                    data-testid={`schedule-editor-inicio-${momento}`}
                  />
                  <span className="text-slate-500 text-xs">–</span>
                  <Input
                    value={h.hora_fin}
                    onChange={(e) => updateHorario(momento, 'hora_fin', e.target.value)}
                    placeholder="HH:MM"
                    className="bg-slate-900 border-slate-700 text-white text-xs h-8"
                    data-testid={`schedule-editor-fin-${momento}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-xs text-slate-400 mb-2">Observaciones operativas (avisos de montaje, restricciones, etc.)</p>
        <div className="space-y-2">
          {observaciones.map((o, idx) => (
            <div
              key={o.id || idx}
              className="flex items-start gap-2 bg-slate-800/50 border border-slate-700/50 rounded-lg p-2"
              data-testid={`schedule-editor-obs-row-${idx}`}
            >
              <div className="flex-1 space-y-1.5">
                <Input
                  value={o.texto}
                  onChange={(e) => updateObs(idx, 'texto', e.target.value)}
                  placeholder="Ej: Baile en jardín"
                  className="bg-slate-900 border-slate-700 text-white text-sm"
                  data-testid={`schedule-editor-obs-texto-${idx}`}
                />
                <label className="flex items-center gap-1.5 text-xs text-slate-400">
                  <input
                    type="checkbox"
                    checked={!!o.relevante}
                    onChange={(e) => updateObs(idx, 'relevante', e.target.checked)}
                    className="accent-violet-500"
                    data-testid={`schedule-editor-obs-relevante-${idx}`}
                  />
                  Relevante para el montaje del DJ
                </label>
                {o.relevante && (
                  <Input
                    value={o.implicacion}
                    onChange={(e) => updateObs(idx, 'implicacion', e.target.value)}
                    placeholder="Implicación operativa (ej: monta sonido propio en el jardín)"
                    className="bg-slate-900 border-slate-700 text-amber-200 text-xs"
                    data-testid={`schedule-editor-obs-implicacion-${idx}`}
                  />
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeObs(idx)}
                data-testid={`schedule-editor-obs-remove-${idx}`}
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addObs}
            className="border-slate-600 text-slate-300"
            data-testid="schedule-editor-obs-add"
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir observación
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ============== VISOR (solo lectura, ficha del evento) ============== */

/** Solo la barra de horarios (marco general de los 5 momentos fijos + extra). */
export const EventScheduleTimelineBar = ({ horarios = [] }) => {
  const fixed = FIXED_MOMENTS
    .map((m) => (horarios || []).find((h) => h.momento === m && !h.es_extra))
    .filter((h) => h && (h.hora_inicio || h.hora_fin));
  const extras = (horarios || [])
    .filter((h) => h.es_extra && (h.hora_inicio || h.hora_fin))
    .sort((a, b) => (a.hora_inicio || '99:99').localeCompare(b.hora_inicio || '99:99'));
  const filled = [...fixed, ...extras];
  if (filled.length === 0) return null;
  return (
    <div className="flex items-stretch overflow-x-auto pb-1 -mx-1 px-1" data-testid="event-schedule-timeline">
      {filled.map((h, idx) => {
        const { Icon, color, bg } = getFaseIcon(h.momento);
        return (
          <div className="flex items-center shrink-0" key={`${h.momento}-${idx}`}>
            {idx > 0 && (
              <div className="w-5 sm:w-7 h-0.5 bg-gradient-to-r from-slate-600 to-slate-500/60 shrink-0" />
            )}
            <div
              className={`flex flex-col gap-0.5 rounded-xl border px-3 py-2 min-w-[140px] shrink-0 ${bg} ${
                h.es_extra ? 'border-dashed border-amber-400/50' : 'border-white/10'
              }`}
              data-testid={`event-schedule-momento-${idx}`}
            >
              <div className="flex items-center gap-1.5">
                <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                <span className={`text-xs font-semibold truncate ${color}`}>{h.momento}</span>
                {h.es_extra && (
                  <span className="text-[9px] px-1 py-0.5 bg-amber-500/20 text-amber-300 rounded shrink-0">EXTRA</span>
                )}
              </div>
              <span className="text-white text-sm font-medium flex items-center gap-1">
                <Clock className="w-3 h-3 shrink-0" />
                {h.hora_inicio || '–'}{h.hora_fin ? `–${h.hora_fin}` : ''}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Solo la lista de observaciones operativas (relevantes destacadas en ámbar).
 * Si `interactive` es true y se pasa `onToggleCompletado`, las relevantes se
 * muestran como checklist de montaje marcable (Fase 3). */
export const EventObservacionesList = ({ observaciones = [], interactive = false, onToggleCompletado }) => {
  const relevantes = (observaciones || []).filter((o) => o.relevante);
  const informativas = (observaciones || []).filter((o) => !o.relevante);
  if ((observaciones || []).length === 0) return null;
  return (
    <div className="space-y-1.5" data-testid="event-observaciones-display">
      {relevantes.map((o, idx) => (
        <div
          key={o.id || idx}
          className={`flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 ${
            o.completado ? 'opacity-50' : ''
          }`}
          data-testid={`event-observacion-relevante-${idx}`}
        >
          {interactive && o.id ? (
            <Checkbox
              checked={!!o.completado}
              onCheckedChange={(checked) => onToggleCompletado && onToggleCompletado(o.id, !!checked)}
              className="mt-0.5 shrink-0 border-amber-400"
              data-testid={`checklist-item-${o.id}`}
            />
          ) : (
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <p className={`text-amber-200 text-sm font-medium ${o.completado ? 'line-through' : ''}`}>{o.texto}</p>
            {o.implicacion && <p className="text-amber-300/80 text-xs mt-0.5">{o.implicacion}</p>}
          </div>
        </div>
      ))}
      {informativas.map((o, idx) => (
        <div
          key={o.id || idx}
          className="flex items-start gap-2 bg-slate-800/40 border border-slate-700/40 rounded-lg px-3 py-2"
          data-testid={`event-observacion-informativa-${idx}`}
        >
          <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
          <p className="text-slate-300 text-sm">{o.texto}</p>
        </div>
      ))}
    </div>
  );
};

/** Composición de ambas (compat / usos que quieran todo junto). */
export const EventScheduleNotesDisplay = ({ horarios = [], observaciones = [] }) => {
  if (
    FIXED_MOMENTS.every((m) => !(horarios || []).find((h) => h.momento === m && (h.hora_inicio || h.hora_fin))) &&
    (observaciones || []).length === 0
  ) {
    return null;
  }
  return (
    <div className="space-y-3" data-testid="event-schedule-notes-display">
      <EventScheduleTimelineBar horarios={horarios} />
      <EventObservacionesList observaciones={observaciones} />
    </div>
  );
};

/* ============== Secciones de REVISIÓN (checkboxes, documentos y emails) ============== */
export const ScheduleNotesReviewSections = ({
  horarios = [],
  selectedHorarios,
  onToggleHorario,
  observaciones = [],
  selectedObservaciones,
  onToggleObservacion,
}) => {
  if (horarios.length === 0 && observaciones.length === 0) return null;
  return (
    <div className="space-y-4" data-testid="schedule-notes-review-sections">
      {horarios.length > 0 && (
        <div data-testid="review-horarios-section">
          <p className="text-sm font-semibold text-violet-300 mb-2 flex items-center gap-1.5">
            <Clock className="w-4 h-4" /> Horarios detectados ({horarios.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {horarios.map((h, idx) => {
              const { Icon, color, bg } = getFaseIcon(h.momento);
              const checked = selectedHorarios.has(idx);
              return (
                <label
                  key={idx}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${bg} ${
                    checked ? 'border-violet-400' : 'border-white/10'
                  }`}
                  data-testid={`review-horario-${idx}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleHorario(idx)}
                    className="accent-violet-500"
                    data-testid={`review-horario-checkbox-${idx}`}
                  />
                  <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                  <span className="min-w-0 flex-1">
                    <span className={`text-xs font-semibold ${color}`}>{h.momento}</span>
                    <span className="text-white text-sm ml-2">
                      {h.hora_inicio}{h.hora_fin ? `–${h.hora_fin}` : ''}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {observaciones.length > 0 && (
        <div data-testid="review-observaciones-section">
          <p className="text-sm font-semibold text-violet-300 mb-2 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> Observaciones detectadas ({observaciones.length})
          </p>
          <div className="space-y-1.5">
            {observaciones.map((o, idx) => {
              const checked = selectedObservaciones.has(idx);
              return (
                <label
                  key={idx}
                  className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${
                    o.relevante ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/40 border-slate-700/40'
                  } ${checked ? 'ring-1 ring-violet-400' : ''}`}
                  data-testid={`review-observacion-${idx}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleObservacion(idx)}
                    className="accent-violet-500 mt-0.5"
                    data-testid={`review-observacion-checkbox-${idx}`}
                  />
                  {o.relevante ? (
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  ) : (
                    <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0">
                    <p className={`text-sm font-medium ${o.relevante ? 'text-amber-200' : 'text-slate-300'}`}>
                      {o.texto}
                    </p>
                    {o.relevante && o.implicacion && (
                      <p className="text-amber-300/80 text-xs mt-0.5">{o.implicacion}</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============== Fase 3: Momentos EXTRA (revisión) ============== */
export const ExtraMomentsReviewSection = ({
  momentosExtra = [],
  selectedExtra,
  onToggleExtra,
}) => {
  if (!momentosExtra || momentosExtra.length === 0) return null;
  return (
    <div data-testid="review-momentos-extra-section">
      <p className="text-sm font-semibold text-amber-300 mb-2 flex items-center gap-1.5">
        <CalendarClock className="w-4 h-4" /> Momentos extra detectados ({momentosExtra.length})
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {momentosExtra.map((m, idx) => {
          const checked = selectedExtra.has(idx);
          return (
            <label
              key={idx}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer bg-amber-500/10 ${
                checked ? 'border-amber-400' : 'border-white/10'
              }`}
              data-testid={`review-momento-extra-${idx}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggleExtra(idx)}
                className="accent-amber-500"
                data-testid={`review-momento-extra-checkbox-${idx}`}
              />
              <CalendarClock className="w-4 h-4 shrink-0 text-amber-300" />
              <span className="min-w-0 flex-1">
                <span className="text-xs font-semibold text-amber-300">{m.nombre}</span>
                <span className="text-white text-sm ml-2">
                  {m.hora_inicio}{m.hora_fin ? `–${m.hora_fin}` : ''}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

/* ============== Fase 3: Reunión previa detectada ============== */
export const ReunionDetectedCard = ({ reunion, enabled, onToggleEnabled, fecha, hora, onChangeFecha, onChangeHora }) => {
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  if (!reunion || !reunion.detectada) return null;
  const selectedDate = fecha ? parseISO(fecha) : undefined;
  return (
    <div
      className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 p-3 space-y-2"
      data-testid="reunion-detected-card"
    >
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggleEnabled(e.target.checked)}
          className="accent-cyan-500 mt-0.5"
          data-testid="reunion-detected-checkbox"
        />
        <span className="min-w-0">
          <span className="text-cyan-300 text-sm font-semibold flex items-center gap-1.5">
            <CalendarClock className="w-4 h-4" /> Reunión previa detectada — crear tarea en la agenda
          </span>
          {reunion.texto && <span className="text-cyan-200/80 text-xs block mt-0.5">{reunion.texto}</span>}
        </span>
      </label>
      {enabled && (
        <div className="flex items-center gap-2 pl-6">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={`h-8 text-xs justify-start gap-1.5 bg-slate-900 hover:bg-slate-800 ${
                  fecha ? 'text-white border-slate-700' : 'text-amber-300 border-amber-500/50'
                }`}
                data-testid="reunion-detected-fecha"
              >
                <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
                {fecha ? format(selectedDate, "d MMM yyyy", { locale: es }) : 'Elegir fecha'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="start" data-testid="reunion-detected-fecha-calendar">
              <Calendar
                mode="single"
                selected={selectedDate}
                defaultMonth={selectedDate}
                onSelect={(date) => {
                  if (date) onChangeFecha(format(date, 'yyyy-MM-dd'));
                  setCalendarOpen(false);
                }}
                locale={es}
                initialFocus
                className="text-white"
                classNames={{
                  caption_label: 'text-sm font-medium text-white',
                  head_cell: 'text-slate-400 rounded-md w-8 font-normal text-[0.8rem]',
                  day: 'h-8 w-8 p-0 font-normal text-slate-200 hover:bg-slate-700 hover:text-white aria-selected:opacity-100',
                  day_selected: 'bg-cyan-500 text-slate-950 hover:bg-cyan-500 hover:text-slate-950 focus:bg-cyan-500 focus:text-slate-950',
                  day_today: 'bg-slate-700 text-white',
                  day_outside: 'day-outside text-slate-600 aria-selected:bg-accent/50',
                  nav_button: 'h-7 w-7 bg-transparent p-0 text-slate-300 opacity-70 hover:opacity-100 hover:bg-slate-700',
                }}
              />
            </PopoverContent>
          </Popover>
          <Input
            value={hora}
            onChange={(e) => onChangeHora(e.target.value)}
            placeholder="HH:MM"
            className="bg-slate-900 border-slate-700 text-white text-xs h-8 w-20"
            data-testid="reunion-detected-hora"
          />
          {!fecha && <span className="text-amber-300 text-xs">Introduce la fecha</span>}
        </div>
      )}
    </div>
  );
};

export default EventScheduleNotesDisplay;
